import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Individual sortable service line item
function SortableServiceLineItem({ 
  line, 
  index, 
  services, 
  onUpdate, 
  onRemove, 
  onServiceSelect,
  showQuantityPrompt 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: line.id || index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`p-3 bg-slate-50 rounded-lg border space-y-3 ${isDragging ? 'shadow-lg ring-2 ring-amber-400' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 rounded touch-none"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </button>
          <span className="text-xs font-medium text-slate-500">Line {index + 1}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-red-500 h-6"
          onClick={() => onRemove(index)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div className="sm:col-span-2">
          <select
            value={line.service_type?.split(':')[0]?.trim() || ''}
            onChange={(e) => onServiceSelect(index, e.target.value, line)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm"
          >
            <option value="">Select Service</option>
            {services.filter(s => s.active !== false).map(s => (
              <option key={s.id} value={s.name}>
                {s.name} {s.day_price ? `($${s.day_price}/day)` : ''} {s.hourly_price ? `($${s.hourly_price}/hr)` : ''} {s.per_hole_price ? `($${s.per_hole_price}/hole)` : ''}
              </option>
            ))}
          </select>
          {/* Display the full service type with quantity if applicable */}
          {line.service_type && line.service_type.includes(':') && (
            <p className="text-xs text-blue-600 mt-1 font-medium">{line.service_type}</p>
          )}
        </div>
        <div className="sm:col-span-2">
          <Input
            value={line.description || ''}
            onChange={(e) => onUpdate(index, { description: e.target.value })}
            placeholder="Description"
            className="h-9 bg-white"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-xs">Rate Type</Label>
          {(() => {
            const currentService = services.find(s => s.name === line.service_type?.split(':')[0]?.trim());
            const hasPerHole = currentService?.per_hole_price;
            return (
              <select
                value={line.rate_type || 'day'}
                onChange={(e) => {
                  const rateType = e.target.value;
                  const selectedService = services.find(s => s.name === line.service_type?.split(':')[0]?.trim());
                  let price = line.unit_price || 0;
                  
                  if (selectedService) {
                    if (rateType === 'day' && selectedService.day_price) {
                      price = parseFloat(selectedService.day_price) || 0;
                    } else if (rateType === 'hour' && selectedService.hourly_price) {
                      price = parseFloat(selectedService.hourly_price) || 0;
                    } else if (rateType === 'hole' && selectedService.per_hole_price) {
                      price = parseFloat(selectedService.per_hole_price) || 0;
                    } else if (rateType === 'fixed' && selectedService.fixed_price) {
                      price = parseFloat(selectedService.fixed_price) || 0;
                    } else if (rateType === 'night' && selectedService.night_price) {
                      price = parseFloat(selectedService.night_price) || 0;
                    } else if (rateType === 'linear_footage' && selectedService.linear_footage_price) {
                      price = parseFloat(selectedService.linear_footage_price) || 0;
                    }
                  }
                  
                  onUpdate(index, { 
                    rate_type: rateType, 
                    unit_price: price,
                    total: (line.quantity || 1) * price 
                  });
                }}
                className="flex h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm"
              >
                <option value="hour">Per Hour</option>
                <option value="day">Per Day</option>
                <option value="night">Per Night</option>
                {hasPerHole && <option value="hole">Per Hole</option>}
                <option value="linear_footage">Linear Footage</option>
                <option value="fixed">Fixed</option>
              </select>
            );
          })()}
        </div>
        <div>
          <Label className="text-xs">Qty ({line.rate_type === 'hour' ? 'hrs' : line.rate_type === 'day' ? 'days' : line.rate_type === 'night' ? 'nights' : line.rate_type === 'hole' ? 'holes' : line.rate_type === 'linear_footage' ? 'LF' : 'units'})</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={line.quantity ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              const qty = val === '' ? '' : parseFloat(val);
              onUpdate(index, { 
                quantity: qty, 
                total: (parseFloat(qty) || 0) * (parseFloat(line.unit_price) || 0) 
              });
            }}
            className="h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-xs">Unit Price ($/{line.rate_type === 'hour' ? 'hr' : line.rate_type === 'day' ? 'day' : line.rate_type === 'night' ? 'night' : line.rate_type === 'hole' ? 'hole' : line.rate_type === 'linear_footage' ? 'LF' : 'unit'})</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={line.unit_price ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              const price = val === '' ? '' : parseFloat(val);
              onUpdate(index, { 
                unit_price: price, 
                total: (parseFloat(line.quantity) || 0) * (parseFloat(price) || 0) 
              });
            }}
            placeholder="0.00"
            className="h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-xs">Line Total</Label>
          <div className="h-9 flex items-center px-3 bg-green-50 rounded-md border font-medium text-green-700">
            ${(line.total || 0).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main sortable service lines component
export default function SortableServiceLines({
  serviceLines,
  services,
  onServiceLinesChange,
  onShowQuantityDialog,
  emptyServiceLine
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = serviceLines.findIndex(item => (item.id || serviceLines.indexOf(item)) === active.id);
      const newIndex = serviceLines.findIndex(item => (item.id || serviceLines.indexOf(item)) === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newLines = arrayMove(serviceLines, oldIndex, newIndex);
        onServiceLinesChange(newLines);
      }
    }
  };

  const handleUpdate = (index, updates) => {
    const newLines = serviceLines.map((l, i) => 
      i === index ? { ...l, ...updates } : l
    );
    onServiceLinesChange(newLines);
  };

  const handleRemove = (index) => {
    const newLines = serviceLines.filter((_, i) => i !== index);
    onServiceLinesChange(newLines);
  };

  const handleServiceSelect = (index, serviceName, line) => {
    const selectedService = services.find(s => s.name === serviceName);
    const rateType = line.rate_type || 'day';
    let price = line.unit_price || 0;
    
    if (selectedService) {
      if (rateType === 'day' && selectedService.day_price) {
        price = parseFloat(selectedService.day_price) || 0;
      } else if (rateType === 'hour' && selectedService.hourly_price) {
        price = parseFloat(selectedService.hourly_price) || 0;
      } else if (rateType === 'hole' && selectedService.per_hole_price) {
        price = parseFloat(selectedService.per_hole_price) || 0;
      } else if (rateType === 'fixed' && selectedService.fixed_price) {
        price = parseFloat(selectedService.fixed_price) || 0;
      } else if (rateType === 'night' && selectedService.night_price) {
        price = parseFloat(selectedService.night_price) || 0;
      } else if (rateType === 'linear_footage' && selectedService.linear_footage_price) {
        price = parseFloat(selectedService.linear_footage_price) || 0;
      }
    }
    
    if (selectedService?.requires_quantity) {
      onShowQuantityDialog({
        serviceIndex: index,
        serviceName: serviceName,
        quantityLabel: selectedService.quantity_label || 'Units',
        quantityPrompt: selectedService.quantity_prompt || 'Enter quantity',
        quantity: 1,
        price: price,
        rateType: rateType
      });
    } else {
      handleUpdate(index, { 
        service_type: serviceName,
        unit_price: price,
        total: (line.quantity || 1) * price
      });
    }
  };

  const handleAddLine = () => {
    const newLine = { ...emptyServiceLine, id: Date.now() };
    onServiceLinesChange([...serviceLines, newLine]);
  };

  // Ensure all items have IDs for drag-and-drop
  const itemsWithIds = serviceLines.map((line, index) => ({
    ...line,
    id: line.id || `line-${index}-${Date.now()}`
  }));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={itemsWithIds.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {itemsWithIds.map((line, index) => (
            <SortableServiceLineItem
              key={line.id}
              line={line}
              index={index}
              services={services}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              onServiceSelect={handleServiceSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
